import React, { useCallback, useEffect, useState } from 'react';
import { FileMinus2, FilePlus2 } from 'lucide-react';
import { ghost, rs, useApi } from '../../../pages/admin/purchasing/ui';
import { NoteDialog } from './NoteDialog';
import { NoteRow } from './NoteActions';

// "Notes" section on a GST invoice: linked notes and the net after notes.
export const InvoiceNotes = ({ invoice, canEdit, onChanged }) => {
  const { call } = useApi();
  const [ctx, setCtx] = useState(null);
  const [reasons, setReasons] = useState(null);
  const [open, setOpen] = useState('');
  const load = useCallback(() => call('get', `/notes/invoice/${invoice.id}`).then(setCtx).catch(() => {}), [call, invoice.id]);
  useEffect(() => { load(); call('get', '/notes/reasons').then(setReasons).catch(() => {}); }, [load, call]);
  if (!ctx) return null;
  const issuable = !['draft', 'cancelled'].includes(invoice.status);
  const after = () => { setOpen(''); load(); onChanged?.(); };
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="invoice-notes">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-slate-900">Credit &amp; debit notes</h2>
        {canEdit && issuable && reasons && <div className="ml-auto flex gap-2">
          <button type="button" className={`${ghost} !text-emerald-700 !border-emerald-300`} onClick={() => setOpen('credit')} data-testid="invoice-credit-note-btn"><FileMinus2 size={14} /> Credit note</button>
          <button type="button" className={`${ghost} !text-amber-700 !border-amber-300`} onClick={() => setOpen('debit')} data-testid="invoice-debit-note-btn"><FilePlus2 size={14} /> Debit note</button>
        </div>}
      </div>
      {!issuable && <p className="text-xs text-slate-500">Notes can be raised once the invoice is issued.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="invoice-notes-summary">
        {[['Invoice total', ctx.grand_total, 'invoice-notes-original'], ['Net after notes', ctx.net_total, 'invoice-notes-net'],
          ['Paid (net of refunds)', ctx.paid, 'invoice-notes-paid'], ['Balance due', ctx.balance, 'invoice-notes-balance']].map(([k, v, t]) => (
          <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="font-mono font-semibold" data-testid={t}>{rs(v)}</div></div>
        ))}
      </div>
      {ctx.customer_credit > 0 && <p className="text-xs text-emerald-700" data-testid="invoice-customer-credit">This customer has {rs(ctx.customer_credit)} credit, used automatically on their next invoice.</p>}
      {ctx.notes.length === 0 ? <p className="text-xs text-slate-400" data-testid="invoice-notes-empty">No notes on this invoice.</p>
        : <div className="space-y-2">{ctx.notes.map((n) => <NoteRow key={n.id} n={n} canEdit={canEdit} onChanged={after} />)}</div>}
      {open && <NoteDialog invoice={invoice} kind={open} ctx={ctx} reasons={reasons.customer[open]} onClose={() => setOpen('')} onSaved={after} />}
    </section>
  );
};
